/**
 * Initial form values for a NEW document: meta defaults (Frappe special
 * defaults resolved), `?link_doctype=&link_name=` prefill, and the user's
 * company for a required Company link.
 *
 * Extracted from StandardDetailPage (file-size split, behaviour unchanged).
 */

import type { DocTypeSchema } from "@/lib/erp/meta";
import { formatTodayAsDate, formatNowAsDatetime, formatNowAsTime } from "@/lib/utils/dateDefaults";

export function buildNewDocDefaults(
  metaSchema: DocTypeSchema,
  doctype: string,
  searchParams: URLSearchParams,
  tenantCompany: string | null,
): Record<string, unknown> {
  // Initialize with defaults from meta
  const defaults: Record<string, unknown> = {};
  for (const field of metaSchema.fields) {
    if (field.default !== undefined && field.default !== null && field.default !== "") {
      // Process Frappe special defaults
      const defaultStr = String(field.default);

      if (field.fieldtype === "Date" && defaultStr === "Today") {
        defaults[field.fieldname] = formatTodayAsDate();
      } else if (field.fieldtype === "Datetime" && defaultStr.toLowerCase() === "now") {
        defaults[field.fieldname] = formatNowAsDatetime();
      } else if (field.fieldtype === "Time" && defaultStr.toLowerCase() === "now") {
        defaults[field.fieldname] = formatNowAsTime();
      } else if (field.fieldtype === "Check") {
        // Check fields must be numeric (0/1) — Frappe's validate() treats "0" (string) as truthy
        defaults[field.fieldname] = Number(field.default) || 0;
      } else if (["Int", "Float", "Currency", "Percent"].includes(field.fieldtype)) {
        defaults[field.fieldname] = Number(field.default) || 0;
      } else {
        defaults[field.fieldname] = field.default;
      }
    }
    // Select fields with a single option and no explicit default:
    // auto-fill with the first (only) option — matches Frappe Desk behavior.
    // Fixes Create button staying disabled for fields like naming_series.
    else if (field.fieldtype === "Select" && field.options) {
      const opts = String(field.options).split("\n").filter(Boolean);
      if (opts.length === 1) {
        defaults[field.fieldname] = opts[0];
      }
    }
  }

  // Apply link_doctype/link_name prefill
  const linkDoctype = searchParams.get("link_doctype");
  const linkName = searchParams.get("link_name");
  
  if (linkDoctype && linkName && metaSchema.fields) {
    // Find Link fields that match link_doctype
    const matchingFields = metaSchema.fields.filter(
      (f) => f.fieldtype === "Link" && f.options === linkDoctype
    );
    
    // If exactly one matching field, prefill it
    if (matchingFields.length === 1) {
      defaults[matchingFields[0].fieldname] = linkName;
    }
    // If multiple matches, log warning but don't guess
    else if (matchingFields.length > 1 && import.meta.env.DEV) {
      console.warn(
        `[StandardDetailPage] Multiple Link fields match link_doctype="${linkDoctype}". ` +
        `Cannot auto-prefill. Fields: ${matchingFields.map((f) => f.fieldname).join(", ")}`
      );
    }
  }
  
  // Set common defaults
  defaults.doctype = doctype;
  defaults.docstatus = 0;

  // Auto-populate required Company Link field from user's tenant (Option B
  // multi-company model — each user has exactly one company via User Permissions).
  // Without this, the Create button stays disabled forever on any doctype with a
  // required `company` field (Stock Entry, Sales Invoice, Journal Entry, etc.)
  // because the wizard flows don't expose a company picker.
  const requiredCompanyField = metaSchema.fields.find(
    (f: any) =>
      f.fieldtype === "Link" &&
      f.options === "Company" &&
      f.reqd === 1
  );
  if (
    requiredCompanyField &&
    tenantCompany &&
    !defaults[requiredCompanyField.fieldname]
  ) {
    defaults[requiredCompanyField.fieldname] = tenantCompany;
  }

  return defaults;
}
